import { footerLinkData, socialLinkData } from "../utils/constants";
import { motion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 text-white px-p83 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/8 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-500/8 rounded-full blur-3xl translate-y-1/2 translate-x-1/2 pointer-events-none" />

      <div className="relative z-10">
        {/* Top Section - CTA and Social */}
        <div className="grid gap-y-6 375:flex items-center justify-between pt-10 500:pt-12 pb-8">
          <motion.div
            className="group cursor-pointer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ scale: 1.02 }}
          >
            <h1 className="font-merry-bold tracking-tight text-xl 500:text-2xl 600:text-3xl">
              <span className="relative inline-block">
                <span className="relative z-10 transition-colors duration-300 group-hover:text-primary-400">
                  Let's Work
                </span>
                <motion.span
                  className="absolute bottom-1 left-0 right-0 h-3 bg-primary-500/20 rounded-full"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </span>{" "}
              <span className="font-bold text-primary-400">Together</span>
            </h1>
            <p className="text-neutral-400 text-sm 500:text-base mt-2 font-poppins-regular">
              Ready to bring your vision to life?
            </p>
          </motion.div>
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {socialLinkData.map(({ id, icon, text, href }, index) => (
              <motion.a
                href={href}
                key={id}
                target="_blank"
                rel="noreferrer"
                className="relative p-3 500:p-4 bg-gradient-to-br from-neutral-800/80 to-neutral-900/80 hover:from-primary-500/20 hover:to-primary-600/10 rounded-xl border border-neutral-700/50 hover:border-primary-500/50 transition-all duration-300 group backdrop-blur-sm"
                whileHover={{ scale: 1.1, y: -4 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <img
                  src={icon}
                  alt={`Follow Nexasoft Infotech on ${text}`}
                  className="w-5 h-5 500:w-6 500:h-6 opacity-80 group-hover:opacity-100 transition-all duration-300"
                  loading="lazy"
                  style={{ 
                    filter: 'brightness(0) invert(1)',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
              </motion.a>
            ))}
          </motion.div>
        </div>

        {/* Divider */}
        <div className="my-8 h-px bg-gradient-to-r from-transparent via-neutral-700/50 to-transparent" />

        {/* Main Content */}
        <div className="gap-8 flex-col 750:flex-row 750:flex justify-between pb-10">
          {/* Company Info */}
          <motion.div
            className="750:max-w-80 w-full"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                className="relative"
                whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="/logo.png"
                  alt="Nexasoft Infotech - Software Development Company Logo"
                  className="w-10 500:w-12"
                  loading="lazy"
                />
              </motion.div>
              <h1 className="text-2xl 500:text-3xl font-poppins-bold bg-gradient-to-r from-white via-primary-300 to-primary-400 bg-clip-text text-transparent">
                NEXASOFT
              </h1>
            </div>
            <p className="text-neutral-300 text-sm 500:text-base pt-2 font-poppins-regular leading-relaxed">
              Unlocking Digital Brilliance | Nexasoft - Your Partner in Creative
              Solutions
            </p>
            <motion.a
              href="#contact"
              className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-poppins-semi-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-primary-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Get Started</span>
              <FiArrowRight className="w-4 h-4" />
            </motion.a>
          </motion.div>

          {/* Links Sections */}
          <div className="grid grid-cols-2 gap-y-8 500:flex w-full 750:w-[65%] justify-around 750:pt-0 pt-6">
            {[
              { title: "Quick Links", links: footerLinkData.quickLinks },
              { title: "Information", links: footerLinkData.information },
              { title: "Services", links: footerLinkData.services },
            ].map((section, sectionIndex) => (
              <motion.div
                key={section.title}
                className="min-w-[140px]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: sectionIndex * 0.1 }}
              >
                <h3 className="font-poppins-semi-bold pb-4 text-base 500:text-lg text-neutral-100 mb-2">
                  {section.title}
                </h3>
                <div className="grid gap-3">
                  {section.links.map(({ id, linkName, href }) => (
                    <motion.a
                      href={href}
                      key={id}
                      className="text-neutral-400 font-poppins-regular hover:text-primary-400 transition-colors duration-300 text-sm 500:text-base flex items-center gap-2 group"
                      whileHover={{ x: 5 }}
                    >
                      <span>{linkName}</span>
                      <FiArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 h-px bg-gradient-to-r from-transparent via-neutral-700/50 to-transparent" />

        {/* Copyright */}
        <motion.div
          className="grid gap-y-3 515:flex justify-between items-center text-neutral-400 text-sm 500:text-base pb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-poppins-regular">
            Copyright ©{currentYear} by{" "}
            <span className="text-primary-400 font-poppins-semi-bold">
              Nexasoft Infotech
            </span>
            . All rights reserved.
          </div>
          <div className="flex items-center gap-2 text-neutral-500 text-xs">
            <span>Made with</span>
            <motion.span
              className="text-red-500"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              ❤️
            </motion.span>
            <span>in India</span>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
